
import Vue from "vue";
import { api, BudgetAllocation, BudgetBucket, BudgetBucketRule, ExpressionMatcher } from "@/services/api";
import { formatBudgetRuleMatchers, parseBudgetRuleMatchers } from "@/services/budgetRuleText";

const blankMatcher = (): ExpressionMatcher => ({
    type: "Tag",
    expression: ""
});

type EditableBudgetBucketRule = BudgetBucketRule & {
    matchersText: string;
    textError: string;
};

export default Vue.extend({
    data() {
        return {
            buckets: [] as BudgetBucket[],
            allocations: [] as BudgetAllocation[],
            selectedYear: new Date().getFullYear(),
            rules: [] as EditableBudgetBucketRule[]
        };
    },
    async mounted() {
        await this.refresh();
    },
    methods: {
        async refresh() {
            this.buckets = await api.getBudgetBuckets();
            this.allocations = await api.getBudgetAllocations(this.selectedYear);
            this.rules = (await api.getBudgetBucketRules()).map(this.toEditableRule);
        },
        async refreshAllocations() {
            this.allocations = await api.getBudgetAllocations(this.selectedYear);
        },
        async saveAll() {
            const pendingRules: BudgetBucketRule[] = [];
            for (let index = 0; index < this.rules.length; index++) {
                const rule = this.rules[index];
                try {
                    rule.matchers = parseBudgetRuleMatchers(rule.matchersText);
                    rule.textError = "";
                    pendingRules.push({
                        id: rule.id,
                        budgetBucketId: rule.budgetBucketId,
                        priority: index + 1,
                        description: rule.description,
                        isActive: rule.isActive,
                        matchers: rule.matchers.map(group => group.map(matcher => ({ ...matcher })))
                    });
                } catch (e) {
                    rule.textError = e instanceof Error ? e.message : "Invalid rule text";
                }
            }
            if (this.rules.some(rule => rule.textError)) {
                return;
            }
            const pendingAllocations = this.allocations
                .filter(allocation => allocation.budgetBucketId > 0)
                .map(allocation => ({ ...allocation, year: this.selectedYear }));
            await api.setBudgetBuckets(this.buckets);
            this.buckets = await api.getBudgetBuckets();
            await api.setBudgetAllocations(pendingAllocations, this.selectedYear);
            await api.setBudgetBucketRules(pendingRules.filter(rule => rule.budgetBucketId > 0));
            await this.refresh();
        },
        addBucket() {
            this.buckets.push({
                id: 0,
                name: "",
                slug: "",
                isActive: true
            });
        },
        deleteBucket(bucket: BudgetBucket) {
            const index = this.buckets.indexOf(bucket);
            if (index >= 0) {
                this.buckets.splice(index, 1);
            }
        },
        addAllocation() {
            this.allocations.push({
                id: 0,
                budgetBucketId: this.persistedActiveBuckets[0]?.id || 0,
                year: this.selectedYear,
                annualLimitCents: null,
                monthlyLimitCents: null
            });
        },
        deleteAllocation(allocation: BudgetAllocation) {
            const index = this.allocations.indexOf(allocation);
            if (index >= 0) {
                this.allocations.splice(index, 1);
            }
        },
        addRule() {
            this.rules.push({
                id: 0,
                budgetBucketId: this.persistedActiveBuckets[0]?.id || 0,
                priority: this.rules.length + 1,
                description: "",
                isActive: true,
                matchers: [[blankMatcher()]],
                matchersText: formatBudgetRuleMatchers([[blankMatcher()]]),
                textError: ""
            });
        },
        deleteRule(rule: EditableBudgetBucketRule) {
            const index = this.rules.indexOf(rule);
            if (index >= 0) {
                this.rules.splice(index, 1);
            }
        },
        moveRule(index: number, direction: number) {
            const target = index + direction;
            if (target < 0 || target >= this.rules.length) {
                return;
            }
            const [rule] = this.rules.splice(index, 1);
            this.rules.splice(target, 0, rule);
        },
        parseAmount(event: Event): number | null {
            const value = (event.target as HTMLInputElement).value;
            if (value.trim() === "") {
                return null;
            }
            return Math.round(Number(value) * 100);
        },
        amountValue(value: number | null): string {
            if (value === null || value === undefined) {
                return "";
            }
            return (value / 100).toFixed(2);
        },
        bucketKey(bucket: BudgetBucket): string {
            return `${bucket.id}:${bucket.slug}`;
        },
        allocationKey(allocation: BudgetAllocation): string {
            return `${allocation.id}:${allocation.budgetBucketId}:${allocation.year}`;
        },
        ruleKey(rule: BudgetBucketRule, index: number): string {
            return `${index}:${rule.id}:${rule.description}`;
        },
        toEditableRule(rule: BudgetBucketRule): EditableBudgetBucketRule {
            return {
                ...rule,
                matchersText: formatBudgetRuleMatchers(rule.matchers),
                textError: ""
            };
        }
    },
    computed: {
        persistedActiveBuckets(): BudgetBucket[] {
            return this.buckets.filter(bucket => bucket.isActive && bucket.id > 0);
        }
    }
});
